import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { formatAUMobile } from '@/lib/formatters';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Users, Plus, MoreVertical, Car, Edit, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Customers() {
  const navigate = useNavigate();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  // Funnel filter: 'all' (default) | 'lead' | 'draft' | 'quote' | 'invoice'.
  // 'lead' shows un-promoted booking_leads AND legacy customers with no
  // invoices on file. 'draft' / 'quote' / 'invoice' show only the matching
  // tier within the customers table.
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  
  const [formData, setFormData] = useState({
    display_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchCustomers();
    }
  }, [user, page, search, sortBy, sortOrder, statusFilter]);

  // Whenever the search query, sort, or status filter changes, reset to
  // page 1 so we never request a page past the new total.
  useEffect(() => {
    setPage(1);
  }, [search, sortBy, sortOrder, statusFilter]);

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(search && { search }),
        // Pass funnel filter through. 'all' means don't filter so we OMIT
        // the param (BE treats absent as no filter).
        ...(statusFilter !== 'all' && { status_filter: statusFilter }),
      });
      
      const siteParams = getSiteParams();
      const url = `${BACKEND_URL}/api/customers${siteParams}${siteParams ? '&' : '?'}${params}`;

      // When the user picks 'lead' or 'all', also fetch un-promoted
      // booking_leads — those rows live in a separate table. For any other
      // status filter ('draft' / 'quote' / 'invoice'), leads are NOT part
      // of that funnel tier, so skip the leads fetch entirely.
      const shouldFetchLeads = statusFilter === 'all' || statusFilter === 'lead';
      const leadsParams = new URLSearchParams({
        page: '1',
        page_size: '100',
        include_promoted: 'false',
        ...(search && { search }),
      });
      const leadsUrl = `${BACKEND_URL}/api/leads?${leadsParams}`;

      const fetches = [fetch(url, { credentials: 'include' })];
      if (shouldFetchLeads) fetches.push(fetch(leadsUrl, { credentials: 'include' }));
      const responses = await Promise.all(fetches);
      const [customersResp, leadsResp] = responses;

      let customerRows = [];
      let customerTotal = 0;
      let customerTotalPages = 0;
      if (customersResp.ok) {
        const data = await customersResp.json();
        customerRows = (data.customers || []).map(c => ({ ...c, record_type: 'customer' }));
        customerTotal = data.total || 0;
        customerTotalPages = data.total_pages || 0;
      }

      let leadRows = [];
      let leadTotal = 0;
      if (leadsResp && leadsResp.ok) {
        const ld = await leadsResp.json();
        leadRows = (ld.leads || []).map(l => ({
          ...l,
          // Surface lead_id under `customer_id` is intentional — keeps row
          // React key + click handlers simple. record_type='lead' tells
          // action handlers to route to the lead detail page instead.
          customer_id: l.lead_id,
          record_type: 'lead',
          customer_status: 'lead',
        }));
        leadTotal = ld.total || 0;
      }

      // Surface leads on PAGE 1 only (top of list) to keep paginated
      // browsing of real customers intact on later pages.
      const merged = page === 1 ? [...leadRows, ...customerRows] : customerRows;
      setCustomers(merged);
      setTotal(customerTotal + leadTotal);
      setTotalPages(Math.max(customerTotalPages, 1));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/customers${siteParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Customer added successfully');
        setShowAddDialog(false);
        resetForm();
        fetchCustomers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add customer');
      }
    } catch (error) {
      console.error('Failed to add customer:', error);
      toast.error('Failed to add customer');
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/customers/${editingCustomer.customer_id}${siteParams}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Customer updated successfully');
        setEditingCustomer(null);
        resetForm();
        fetchCustomers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update customer');
      }
    } catch (error) {
      console.error('Failed to update customer:', error);
      toast.error('Failed to update customer');
    }
  };

  const handleDeleteCustomer = async (customerId, displayName) => {
    if (!window.confirm(`Delete customer "${displayName}"? This action can be undone.`)) {
      return;
    }
    
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/customers/${customerId}${siteParams}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Customer deleted successfully');
        fetchCustomers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Failed to delete customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  const resetForm = () => {
    setFormData({
      display_name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: ''
    });
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      display_name: customer.display_name || '',
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || ''
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="customers-container">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-['Chivo'] font-bold text-4xl sm:text-5xl text-slate-900 mb-2">
              Customers
            </h1>
            <p className="text-slate-500 text-lg">
              {total} customer{total !== 1 ? 's' : ''} • Page {page} of {totalPages || 1}
            </p>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="h-12 px-6 bg-blue-600 hover:bg-blue-700" data-testid="add-customer-button">
                <Plus className="h-5 w-5 mr-2" />
                Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Customer</DialogTitle>
                <DialogDescription>
                  All fields are optional. Display name will be auto-generated if not provided.
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleAddCustomer} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="h-12"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="h-12"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="display_name">Display Name (Optional)</Label>
                  <Input
                    id="display_name"
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                    placeholder="Auto-generated from name or sequential number"
                    className="h-12"
                    data-testid="display-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="h-12"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: formatAUMobile(e.target.value)})}
                      className="h-12"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="h-12"
                    data-testid="address-input"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 h-12 bg-blue-600 hover:bg-blue-700" data-testid="save-customer-button">
                    Save Customer
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="flex-1 h-12">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, email, mobile, suburb, postcode or company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-input"
            />
          </div>

          {/* Funnel filter — quickly target marketing campaigns by tier.
              All / Lead (no invoice) / Draft / Quote / Invoice (billed). */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="status-filter-group">
            {[
              { value: 'all', label: 'All', color: 'bg-slate-100 text-slate-700' },
              { value: 'lead', label: 'Lead', color: 'bg-amber-100 text-amber-800' },
              { value: 'draft', label: 'Draft', color: 'bg-slate-200 text-slate-800' },
              { value: 'quote', label: 'Quote', color: 'bg-sky-100 text-sky-800' },
              { value: 'invoice', label: 'Invoice', color: 'bg-emerald-100 text-emerald-800' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  statusFilter === opt.value
                    ? `${opt.color} border-current ring-2 ring-offset-1 ring-current/30`
                    : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`status-filter-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="cursor-pointer" onClick={() => handleSort('display_name')}>
                  Display Name {sortBy === 'display_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('email')}>
                  Email {sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('phone')}>
                  Phone {sortBy === 'phone' && (sortOrder === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('mobile')}>
                  Mobile {sortBy === 'mobile' && (sortOrder === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead>Suburb</TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('created_at')}>
                  Created {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No customers found</p>
                    <p className="text-slate-400 text-sm mt-1">Add your first customer to get started</p>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => {
                  const isLead = customer.record_type === 'lead';
                  // Resolve the funnel status. Leads always render as 'lead'
                  // regardless of the field on the doc.
                  const status = isLead ? 'lead' : (customer.customer_status || 'lead');
                  const statusStyles = {
                    lead:    { label: 'Lead',    cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
                    draft:   { label: 'Draft',   cls: 'bg-slate-200 text-slate-800 border border-slate-300' },
                    quote:   { label: 'Quote',   cls: 'bg-sky-100 text-sky-800 border border-sky-200' },
                    invoice: { label: 'Invoice', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
                  };
                  const sty = statusStyles[status] || statusStyles.lead;
                  return (
                  <TableRow
                    key={customer.customer_id}
                    data-testid={isLead ? 'lead-row' : 'customer-row'}
                    className={`cursor-pointer hover:bg-slate-50 ${isLead ? 'bg-amber-50/30' : ''}`}
                    onClick={() => {
                      if (isLead) navigate(`/leads/${customer.customer_id}`);
                      else navigate(`/customers/${customer.customer_id}`);
                    }}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${sty.cls}`}
                          data-testid={`customer-status-${customer.customer_id}`}
                          title={`Funnel status: ${sty.label}`}
                        >
                          {sty.label}
                        </span>
                        <span data-testid={isLead ? `lead-badge-${customer.customer_id}` : `customer-name-${customer.customer_id}`}>{customer.display_name || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{customer.email || '-'}</TableCell>
                    <TableCell className="text-slate-600">{formatAUMobile(customer.phone) || '-'}</TableCell>
                    <TableCell className="text-slate-600 font-mono text-sm" data-testid="customer-mobile">{formatAUMobile(customer.mobile) || '-'}</TableCell>
                    <TableCell className="text-slate-600">{customer.suburb || '-'}</TableCell>
                    <TableCell className="text-slate-600">
                      {new Date(customer.created_at).toLocaleDateString('en-AU')}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid="customer-actions-button">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!isLead && (
                            <>
                              <DropdownMenuItem onClick={() => navigate(`/customers/${customer.customer_id}`)} data-testid="view-vehicles-action">
                                <Car className="h-4 w-4 mr-2" />
                                View Vehicles
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(customer)} data-testid="edit-customer-action">
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            </>
                          )}
                          {isLead && (
                            <DropdownMenuItem onClick={() => navigate(`/leads/${customer.customer_id}`)} data-testid="view-lead-action">
                              <Users className="h-4 w-4 mr-2" />
                              View Lead
                            </DropdownMenuItem>
                          )}
                          {user && user.role === 'super_admin' && !isLead && (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteCustomer(customer.customer_id, customer.display_name)}
                              className="text-red-600"
                              data-testid="delete-customer-action"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );})
              )}
            </TableBody>
          </Table>
        </Card>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} customers
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-10"
                data-testid="prev-page-button"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="flex items-center px-4 text-sm font-medium text-slate-700">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-10"
                data-testid="next-page-button"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {editingCustomer && (
        <Dialog open={!!editingCustomer} onOpenChange={() => { setEditingCustomer(null); resetForm(); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Customer</DialogTitle>
              <DialogDescription>
                Update customer information
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleUpdateCustomer} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_first_name">First Name</Label>
                  <Input
                    id="edit_first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_last_name">Last Name</Label>
                  <Input
                    id="edit_last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="h-12"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit_display_name">Display Name</Label>
                <Input
                  id="edit_display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                  className="h-12"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_phone">Phone</Label>
                  <Input
                    id="edit_phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: formatAUMobile(e.target.value)})}
                    className="h-12"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit_address">Address</Label>
                <Input
                  id="edit_address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="h-12"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 h-12 bg-blue-600 hover:bg-blue-700">
                  Update Customer
                </Button>
                <Button type="button" variant="outline" onClick={() => { setEditingCustomer(null); resetForm(); }} className="flex-1 h-12">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
}
